import { useCallback } from 'react';
import { useFormStore } from '../store/form-stores';

/**
 * Custom hook for managing form-related API calls.
 * Separates API call logic from state management store.
 * 
 * @param workflowId - The workflow identifier (e.g., 'rvm-create', 'mfr-create')
 * @returns Object containing API call methods
 */
export const useFormAPI = (workflowId: string) => {
  const { setAPIStatus } = useFormStore();

  /**
   * Executes an API call and manages its status in the form store.
   * 
   * @param fieldName - The field identifier for tracking API status
   * @param apiCall - Promise-returning function that performs the API call
   * @returns Promise that resolves with the API response
   * 
   * @example
   * const { triggerAPICall } = useFormAPI('rvm-create');
   * 
   * await triggerAPICall(
   *   'dataSource_analysis', 
   *   () => MockDataManager.simulateAPICall('rvm_column_analysis_mock', 2000)
   * );
   */
  const triggerAPICall = useCallback(
    async (fieldName: string, apiCall: () => Promise<any>) => {
      // Set loading state
      setAPIStatus(workflowId, fieldName, { status: 'loading' });
      
      try {
        // Execute the API call
        const response = await apiCall();
        
        // Set success state with response data
        setAPIStatus(workflowId, fieldName, {
          status: 'success',
          data: response.data,
          metadata: response.metadata
        });
        
        return response;
      } catch (error) {
        // Set error state
        setAPIStatus(workflowId, fieldName, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        
        // Re-throw error for caller to handle
        throw error;
      }
    },
    [workflowId, setAPIStatus]
  );

  return {
    triggerAPICall
  };
};
